/* ═══════════════════════════════════════════════════════════════════════════
 * CLASSEMENT EN MASSE — un cran, une liste, on coche, on applique
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ REGLE 41 — le systeme ne decide jamais d'une valeur qui engage
 *    juridiquement l'employeur. Cet ecran ne pre-coche RIEN, ne pre-selectionne
 *    AUCUN cran, et ne propose jamais de « corriger » un agent non classe.
 *    Il rend le geste TENABLE (82 agents ne se classent pas un par un) ; il ne
 *    prend pas la decision a la place des RH.
 *
 * ⚠️ LA DATE D'EFFET EST LE POINT QUI DECIDE DE LA VALEUR DE L'ECRAN.
 *    Proposee : la DATE D'EMBAUCHE de chaque agent, la sienne, pas une date de
 *    lot. Classer aujourd'hui un agent embauche en 2023 ferait demarrer sa
 *    classification en 2026 : son historique de paie deviendrait
 *    injustifiable, et devant un controle CNSS un salaire verse sans
 *    classification qui le couvre est un salaire SANS BASE.
 *    Elle est AFFICHEE et MODIFIABLE ligne a ligne.
 *
 * ⚠️ AUCUN AGENT N'EST ECARTE EN SILENCE. Un agent deja classe est MONTRE avec
 *    son cran et sa date, non cochable, avec sa raison. Le compte rendu final
 *    dit N poses, N ecartes, et la raison de chacun.
 * ═══════════════════════════════════════════════════════════════════════════ */
import { useState, useEffect, useCallback, useMemo } from 'react';
import api from 'services/api';

function jour(d) { return d ? String(d).slice(0, 10) : ''; }
function fmt(d) { return d ? new Date(d).toLocaleDateString('fr-FR') : '—'; }

export default function ClassificationMassePage() {
  const [crans, setCrans] = useState([]);
  const [cransMessage, setCransMessage] = useState('');
  const [agents, setAgents] = useState([]);
  const [postes, setPostes] = useState([]);
  const [filtrePoste, setFiltrePoste] = useState('');
  const [cranChoisi, setCranChoisi] = useState('');
  const [coches, setCoches] = useState({});      // agent_id -> true
  const [dates, setDates] = useState({});        // agent_id -> AAAA-MM-JJ
  const [chargement, setChargement] = useState(true);
  const [envoi, setEnvoi] = useState(false);
  const [compteRendu, setCompteRendu] = useState(null);
  // ⚠️ L'APERCU EST LE VERROU DE L'ECRITURE. `null` = rien n'a ete previsualise,
  //    donc rien ne peut etre applique. Toute modification de la selection, du
  //    cran ou d'une date le remet a `null` : un apercu qui ne correspond plus
  //    a ce qu'on s'apprete a ecrire est pire que pas d'apercu du tout.
  const [apercu, setApercu] = useState(null);
  // Deux gestes sur la MEME liste d'agents : le cran, et le numero d'affilie.
  // Un seul chargement, deux onglets — pas deux ecrans qui divergent.
  const [onglet, setOnglet] = useState('cran');   // 'cran' | 'cnss'
  const [numeros, setNumeros] = useState({});     // agent_id -> numero saisi
  const [compteRenduCnss, setCompteRenduCnss] = useState(null);
  const [postesRef, setPostesRef] = useState([]);
  const [erreur, setErreur] = useState('');

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get('/classifications/agents');
      setAgents(data.agents || []);
      setPostes(data.postes || []);
      // La date PROPOSEE de chaque agent est LA SIENNE. On la pose ici, une
      // fois, et l'utilisateur la modifie ligne a ligne s'il le faut.
      const d = {};
      const num = {};
      (data.agents || []).forEach((a) => {
        d[a.id] = jour(a.date_embauche);
        // On pre-remplit avec le numero DEJA EN BASE, jamais avec un numero
        // fabrique : une case vide reste vide.
        num[a.id] = a.numero_cnss || '';
      });
      setDates(d);
      setNumeros(num);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Chargement impossible.');
    } finally { setChargement(false); }
  }, []);

  useEffect(() => { charger(); }, [charger]);
  // ⚠️ LE FILTRE PAR POSTE LIT LE REFERENTIEL, pas la liste deduite des agents.
  //    `/classifications/agents` rend aussi des `postes`, mais AGREGES DEPUIS
  //    LES AGENTS : un poste que personne n'occupe encore n'y figure pas, et
  //    un code mal saisi y apparaitrait comme s'il existait. Deux sources pour
  //    « quels postes existent » (mesure du 06/09/2026).
  //    ⓘ Les EFFECTIFS, eux, viennent bien des agents : c'est une donnee SUR
  //      les agents, pas sur le referentiel.
  useEffect(() => {
    api.get('/postes')
      .then(({ data }) => setPostesRef(data.postes || []))
      .catch(() => setErreur('Référentiel des postes illisible : le filtre par poste est vide.'));
  }, []);

  useEffect(() => {
    api.get('/classifications/crans')
      .then(({ data }) => { setCrans(data.crans || []); setCransMessage(data.message || ''); })
      .catch(() => setCransMessage('Barème de classification illisible.'));
  }, []);

  const visibles = useMemo(
    () => agents.filter(a => !filtrePoste || a.poste === filtrePoste),
    [agents, filtrePoste]
  );
  const classables = useMemo(() => visibles.filter(a => !a.cran), [visibles]);
  const nbCoches = useMemo(
    () => classables.filter(a => coches[a.id]).length,
    [classables, coches]
  );

  // « Tout cocher » ne coche QUE les classables affichés. Il ne touche jamais
  // un agent déjà classé — cocher ce qu'on ne peut pas appliquer serait
  // promettre un geste qui échouera.
  const toutCocher = (valeur) => {
    const n = { ...coches };
    classables.forEach((a) => { n[a.id] = valeur; });
    setApercu(null);
    setCoches(n);
  };

  // Le lot coché, sous la forme attendue par les deux appels.
  const lotCoche = () => classables
    .filter(a => coches[a.id])
    .map(a => ({ agent_id: a.id, date_debut: dates[a.id] }));

  // ⚠️ PREVISUALISER N'ECRIT RIEN. C'est le seul chemin vers `appliquer` :
  //    tant que `apercu` est nul, le bouton d'ecriture n'existe pas a l'ecran.
  const previsualiser = async () => {
    setEnvoi(true); setErreur(''); setCompteRendu(null); setApercu(null);
    try {
      const { data } = await api.post('/classifications/previsualiser', {
        code: cranChoisi,
        agents: lotCoche(),
      });
      setApercu(data);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Prévisualisation impossible.');
    } finally { setEnvoi(false); }
  };

  const appliquer = async () => {
    // ⚠️ GARDE D'ECRAN : on n'ecrit jamais sans avoir affiche l'apercu, et on
    //    n'envoie QUE les lignes que l'apercu a declarees `validable`. Un agent
    //    dont le poste n'a pas de famille d'emploi tranchee ne part pas dans le
    //    lot — le serveur le refuserait de toute facon, mais l'ecran ne doit pas
    //    le proposer.
    if (!apercu) { setErreur('Prévisualiser avant d’appliquer.'); return; }
    const validables = apercu.lignes.filter(l => l.validable).map(l => l.agent_id);
    const lot = lotCoche().filter(l => validables.includes(l.agent_id));
    if (lot.length === 0) {
      setErreur('Aucun agent validable dans cette sélection. Rien n’a été écrit.');
      return;
    }
    setEnvoi(true); setErreur(''); setCompteRendu(null);
    try {
      const cran = crans.find(c => c.code === cranChoisi);
      const { data } = await api.post('/classifications/appliquer', {
        code: cranChoisi,
        origine: cran ? cran.origine : 'pays',
        agents: lot,
      });
      setCompteRendu(data);
      setCoches({});
      setApercu(null);          // l'apercu ne survit pas a l'ecriture : il est perime
      charger();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Application impossible.');
    } finally { setEnvoi(false); }
  };

  const enregistrerNumeros = async () => {
    setEnvoi(true); setErreur(''); setCompteRenduCnss(null);
    try {
      // On n'envoie QUE ce qui a change et n'est pas vide : une case laissee
      // vide n'efface jamais un numero deja en base.
      const lot = visibles
        .filter(a => (numeros[a.id] || '').trim() && (numeros[a.id] || '').trim() !== (a.numero_cnss || ''))
        .map(a => ({ agent_id: a.id, numero_cnss: numeros[a.id].trim() }));
      if (lot.length === 0) { setErreur('Aucun numéro modifié.'); setEnvoi(false); return; }
      const { data } = await api.post('/classifications/numeros-cnss', { agents: lot });
      setCompteRenduCnss(data);
      charger();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Enregistrement impossible.');
    } finally { setEnvoi(false); }
  };

  const nbNumerosModifies = visibles.filter(
    a => (numeros[a.id] || '').trim() && (numeros[a.id] || '').trim() !== (a.numero_cnss || '')
  ).length;

  const cranActif = crans.find(c => c.code === cranChoisi);
  // Le bouton n'est actif que si les trois conditions du geste sont reunies.
  // Un bouton qui echoue est pire qu'un bouton eteint.
  const datesManquantes = classables.filter(a => coches[a.id] && !dates[a.id]).length;
  const peutAppliquer = cranChoisi && nbCoches > 0 && datesManquantes === 0 && !envoi;

  return (
    <div className="gss-page">
      <div className="gss-page-header">
        <h1>Classement des agents</h1>
        <div className="gss-page-subtitle">
          Catégorie professionnelle — CCIT art. 35. Elle fixe le salaire plancher
          et conditionne la génération du contrat de travail.
        </div>
      </div>

      {/* ── DEUX ONGLETS, UNE SEULE LISTE ────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          className={onglet === 'cran' ? 'gss-btn gss-btn-primary' : 'gss-btn'}
          onClick={() => { setOnglet('cran'); setErreur(''); }}
        >Catégorie professionnelle</button>
        <button
          className={onglet === 'cnss' ? 'gss-btn gss-btn-primary' : 'gss-btn'}
          onClick={() => { setOnglet('cnss'); setErreur(''); }}
        >Numéros d'affilié CNSS</button>
      </div>

      {cransMessage && onglet === 'cran' && (
        <div className="gss-alert" style={{ background: '#FEF3C7', color: '#92400E', padding: 12, borderRadius: 6, marginBottom: 16 }}>
          {cransMessage}
        </div>
      )}
      {erreur && (
        <div className="gss-alert" style={{ background: '#FEE2E2', color: '#991B1B', padding: 12, borderRadius: 6, marginBottom: 16 }}>
          {erreur}
        </div>
      )}

      {/* ── LE GESTE, EN TETE ─────────────────────────────────────────────── */}
      <div className="gss-card" style={{ padding: 16, marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {/* Le filtre par poste sert aux DEUX onglets : les agents de sécurité
            se traitent par paquets, quel que soit le geste. */}
        <div className="gss-field" style={{ minWidth: 200 }}>
          <label>Poste</label>
          <select className="gss-input" value={filtrePoste} onChange={e => { setFiltrePoste(e.target.value); setCoches({}); setApercu(null); }}>
            <option value="">Tous les postes</option>
            {postesRef.map(p => {
              // L'effectif vient des agents ; le libelle et l'existence, du referentiel.
              const n = (postes.find(x => x.poste === p.code) || {}).n || 0;
              return <option key={p.code} value={p.code}>{p.titre} ({n})</option>;
            })}
          </select>
        </div>
        {onglet === 'cran' && (
        <div className="gss-field" style={{ minWidth: 320 }}>
          <label>Catégorie à appliquer *</label>
          <select className="gss-input" value={cranChoisi} onChange={e => { setCranChoisi(e.target.value); setApercu(null); }}>
            {/* Aucun cran pré-sélectionné. Jamais. */}
            <option value="">— choisir une catégorie —</option>
            {crans.map(c => (
              <option key={c.origine + c.code} value={c.code}>
                {c.code} — {c.libelle}
                {c.plancher != null ? ` (${Number(c.plancher).toLocaleString('fr-FR')} F)` : ''}
              </option>
            ))}
          </select>
          {cranActif && cranActif.plancher != null && (
            <small style={{ color: '#555' }}>
              Salaire plancher : {Number(cranActif.plancher).toLocaleString('fr-FR')} F
            </small>
          )}
        </div>
        )}
        {onglet === 'cran' ? (
          <>
            {/* ⚠️ DEUX BOUTONS, JAMAIS UN SEUL. « Prévisualiser » n'écrit rien.
                « Appliquer » n'apparaît QU'APRÈS un aperçu, et disparaît dès
                que la sélection change. On ne peut pas écrire sans avoir vu. */}
            <button className="gss-btn" disabled={!peutAppliquer} onClick={previsualiser}>
              {envoi ? 'Calcul…' : `Prévisualiser ${nbCoches} agent${nbCoches > 1 ? 's' : ''}`}
            </button>
            {apercu && (
              <button
                className="gss-btn gss-btn-primary"
                disabled={envoi || apercu.totaux.validables === 0}
                onClick={appliquer}
              >
                {envoi ? 'Application…' : `Valider et écrire ${apercu.totaux.validables} classement${apercu.totaux.validables > 1 ? 's' : ''}`}
              </button>
            )}
            {datesManquantes > 0 && (
              <small style={{ color: '#991B1B' }}>
                {datesManquantes} agent(s) coché(s) sans date d'effet.
              </small>
            )}
            {!apercu && nbCoches > 0 && (
              <small style={{ color: '#6B7280' }}>
                Rien ne sera écrit tant que la prévisualisation n'a pas été affichée.
              </small>
            )}
          </>
        ) : (
          <button className="gss-btn gss-btn-primary" disabled={nbNumerosModifies === 0 || envoi} onClick={enregistrerNumeros}>
            {envoi ? 'Enregistrement…' : `Enregistrer ${nbNumerosModifies} numéro${nbNumerosModifies > 1 ? 's' : ''}`}
          </button>
        )}
      </div>

      {/* ── APERÇU AVANT ÉCRITURE — ce que le classement CHANGERAIT ───────────
          ⚠️ Rien n'est écrit à ce stade. Chaque ligne dit le plancher à la date,
             le salaire actuel, l'écart, l'ancienneté recalculée et l'impact
             mensuel. Les lignes non validables sont affichées AVEC leur raison
             et exclues du total : additionner l'impact d'agents qu'on ne peut
             pas poser annoncerait une dépense qui n'aura pas lieu. */}
      {apercu && (
        <div className="gss-card" style={{ padding: 16, marginBottom: 16 }}>
          <strong>
            Aperçu — {apercu.totaux.validables} validable(s) · {apercu.totaux.bloques} bloqué(s)
            {' '}sur {apercu.totaux.demandes} · cran {apercu.cran} ({apercu.pays})
          </strong>
          <div style={{ margin: '4px 0 12px', color: '#6B7280', fontSize: 13 }}>
            Impact mensuel des seules lignes validables :{' '}
            <strong>{apercu.totaux.impact_mensuel_total.toLocaleString('fr-FR')} F</strong>
            {' '}— rien n'est encore écrit.
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="gss-table" style={{ fontSize: 13, minWidth: 900 }}>
              <thead>
                <tr>
                  <th>Agent</th><th>Poste</th><th>Plancher à la date</th>
                  <th>Salaire actuel</th><th>Relève au plancher</th>
                  <th>Ancienneté</th><th>Taux</th><th>Prime</th><th>Impact / mois</th>
                </tr>
              </thead>
              <tbody>
                {apercu.lignes.map((l) => (
                  <tr key={l.agent_id || Math.random()}
                      style={l.validable ? undefined : { background: '#FEF2F2' }}>
                    <td>
                      {l.agent || '(agent inconnu)'}
                      {!l.validable && (
                        <div style={{ color: '#991B1B', fontSize: 12, marginTop: 4 }}>
                          {l.bloquants.map((b, i) => <div key={i}>⚠️ {b}</div>)}
                        </div>
                      )}
                    </td>
                    <td>{l.poste || '—'}</td>
                    <td>{l.plancher_a_la_date === null || l.plancher_a_la_date === undefined
                      ? '—' : `${l.plancher_a_la_date.toLocaleString('fr-FR')} F`}</td>
                    <td>{l.salaire_actuel === null || l.salaire_actuel === undefined
                      ? '—' : `${l.salaire_actuel.toLocaleString('fr-FR')} F`}</td>
                    <td>{l.releve_au_plancher === null || l.releve_au_plancher === undefined
                      ? '—'
                      : l.releve_au_plancher === 0
                        ? <span style={{ color: '#6B7280' }}>déjà au-dessus</span>
                        : <strong style={{ color: '#146B43' }}>
                            +{l.releve_au_plancher.toLocaleString('fr-FR')} F
                          </strong>}</td>
                    <td>{l.anciennete_annees === null || l.anciennete_annees === undefined
                      ? '—' : `${l.anciennete_annees} an(s)`}</td>
                    <td>{l.anciennete_taux_pct === null || l.anciennete_taux_pct === undefined
                      ? '—' : `${l.anciennete_taux_pct} %`}</td>
                    <td>{l.prime_anciennete_mensuelle === null || l.prime_anciennete_mensuelle === undefined
                      ? '—' : `${l.prime_anciennete_mensuelle.toLocaleString('fr-FR')} F`}</td>
                    <td>{l.impact_mensuel === null || l.impact_mensuel === undefined
                      ? '—' : <strong>{l.impact_mensuel.toLocaleString('fr-FR')} F</strong>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── COMPTE RENDU — poses ET ecartes, avec la raison de chacun ─────── */}
      {compteRendu && (
        <div className="gss-card" style={{ padding: 16, marginBottom: 16 }}>
          <strong>
            {compteRendu.compte_rendu.poses} posé(s) · {compteRendu.compte_rendu.ecartes} écarté(s)
            {' '}sur {compteRendu.compte_rendu.demandes} demandé(s)
          </strong>
          {compteRendu.poses.length > 0 && (
            <ul style={{ margin: '8px 0 0', color: '#146B43' }}>
              {compteRendu.poses.map(p => (
                <li key={p.agent_id}>{p.agent} → {p.cran} depuis le {fmt(p.date_debut)}</li>
              ))}
            </ul>
          )}
          {/* Aucun ecart n'est tu. */}
          {compteRendu.ecartes.length > 0 && (
            <ul style={{ margin: '8px 0 0', color: '#B45309' }}>
              {compteRendu.ecartes.map((e, i) => (
                <li key={e.agent_id || i}><strong>{e.agent || 'Ligne incomplète'}</strong> — {e.raison}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Compte rendu de la saisie des numeros — memes regles : rien de tu. */}
      {compteRenduCnss && (
        <div className="gss-card" style={{ padding: 16, marginBottom: 16 }}>
          <strong>
            {compteRenduCnss.compte_rendu.poses} numéro(s) enregistré(s)
            {' · '}{compteRenduCnss.compte_rendu.ecartes} écarté(s)
          </strong>
          {compteRenduCnss.ecartes.length > 0 && (
            <ul style={{ margin: '8px 0 0', color: '#B45309' }}>
              {compteRenduCnss.ecartes.map((e, i) => (
                <li key={e.agent_id || i}>{e.agent || 'Ligne incomplète'} — {e.raison}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ── LA LISTE ──────────────────────────────────────────────────────── */}
      <div className="gss-card">
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {onglet === 'cran' && (
            <>
              <button className="gss-btn" onClick={() => toutCocher(true)} disabled={classables.length === 0}>
                Cocher les {classables.length} non classé(s)
              </button>
              <button className="gss-btn" onClick={() => toutCocher(false)} disabled={nbCoches === 0}>
                Tout décocher
              </button>
            </>
          )}
          <span style={{ color: '#555', marginLeft: 'auto' }}>
            {onglet === 'cran'
              ? `${visibles.length} affiché(s) · ${visibles.filter(a => a.cran).length} déjà classé(s) · ${classables.length} à classer`
              : `${visibles.length} affiché(s) · ${visibles.filter(a => a.numero_cnss).length} avec numéro · ${visibles.filter(a => !a.numero_cnss).length} sans numéro`}
          </span>
        </div>

        {chargement ? <div style={{ padding: 16 }}>Chargement…</div> : (
          <table className="gss-table">
            <thead>
              <tr>
                {onglet === 'cran' && <th style={{ width: 36 }}></th>}
                <th>Matricule</th>
                <th>Nom</th>
                <th>Poste</th>
                <th>Embauche</th>
                {onglet === 'cran' ? (
                  <>
                    <th>Catégorie actuelle</th>
                    <th>Applicable depuis</th>
                  </>
                ) : (
                  <th>Numéro d'affilié CNSS</th>
                )}
              </tr>
            </thead>
            <tbody>
              {visibles.map((a) => {
                const dejaClasse = !!a.cran;
                return (
                  <tr key={a.id} style={(onglet === 'cran' && dejaClasse) ? { opacity: 0.55 } : undefined}>
                    {onglet === 'cran' && (
                    <td>
                      {/* Un agent deja classe n'est pas cochable. */}
                      <input
                        type="checkbox"
                        disabled={dejaClasse}
                        checked={!!coches[a.id]}
                        onChange={e => { setApercu(null); setCoches(c => ({ ...c, [a.id]: e.target.checked })); }}
                      />
                    </td>
                    )}
                    <td>{a.matricule}</td>
                    <td>{a.nom} {a.prenom}</td>
                    <td>{(a.poste || '').replace(/_/g, ' ')}</td>
                    <td>{fmt(a.date_embauche)}</td>
                    {onglet === 'cnss' ? (
                      <td>
                        {/* ⚠️ Attribue par la CAISSE : aucune valeur proposee,
                            aucune derivation du matricule. Une case vide reste
                            vide et n'efface rien. */}
                        <input
                          className="gss-input" type="text" style={{ maxWidth: 220 }}
                          value={numeros[a.id] || ''}
                          onChange={e => setNumeros(n => ({ ...n, [a.id]: e.target.value }))}
                        />
                        {!a.numero_cnss && !(numeros[a.id] || '').trim() && (
                          <div style={{ color: '#B45309', fontSize: 12 }}>sans numéro</div>
                        )}
                      </td>
                    ) : (
                    <>
                    <td>
                      {/* ⚠️ « déjà classé — CRAN depuis DATE », et c'est TOUT.
                          Pas d'infobulle, pas de renvoi vers un geste qui
                          n'existe pas — RÈGLE 26 : le produit ne documente pas
                          ses manques à l'écran. */}
                      {dejaClasse ? (
                        <span>
                          <strong>{a.cran}</strong> depuis le {fmt(a.cran_depuis)}
                        </span>
                      ) : (
                        <span style={{ color: '#B45309' }}>non classé</span>
                      )}
                    </td>
                    <td>
                      {dejaClasse ? '—' : (
                        <input
                          className="gss-input" type="date" style={{ maxWidth: 160 }}
                          value={dates[a.id] || ''}
                          onChange={e => { setApercu(null); setDates(d => ({ ...d, [a.id]: e.target.value })); }}
                        />
                      )}
                    </td>
                    </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
